import React from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../notifications';
import { Spinner } from '../../../shared/components/Spinner';
import {
  createWhatsAppAppChannelAccount,
  getWhatsAppAppSession,
  getWhatsAppAppStatus,
  logoutWhatsAppApp,
  refreshWhatsAppAppQr,
} from '../services/channelAccountService';
import { WhatsAppAppSession } from '../types/channelAccount';
import { useChannelAccounts } from '../hooks/useChannelAccounts';
import { ChannelAccountBar } from './ChannelAccountBar';
import styles from './WhatsAppAppConnectorTab.module.css';

interface WhatsAppAppConnectorTabProps {
  canManage: boolean;
  emphasis?: 'default' | 'welcome';
}

const POLL_MS = 2000;
const CONNECTED_POLL_MS = 5000;

const qrImageSrc = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  return value.startsWith('data:') ? value : `data:image/png;base64,${value}`;
};

export const WhatsAppAppConnectorTab: React.FC<WhatsAppAppConnectorTabProps> = ({ canManage, emphasis = 'default' }) => {
  const { t } = useTranslation(['companies', 'admin']);
  const { showToast } = useToast();
  const accounts = useChannelAccounts('WhatsApp', canManage, 'app');

  const [platformConfigured, setPlatformConfigured] = React.useState<boolean | null>(null);
  const [session, setSession] = React.useState<WhatsAppAppSession | null>(null);
  const [loadingSession, setLoadingSession] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [displayName, setDisplayName] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [refreshingQr, setRefreshingQr] = React.useState(false);
  const autoQrAccountId = React.useRef<string | null>(null);
  const welcome = emphasis === 'welcome';

  React.useEffect(() => {
    if (!canManage) {
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const status = await getWhatsAppAppStatus();
        if (!cancelled) {
          setPlatformConfigured(status.configured);
        }
      } catch {
        if (!cancelled) {
          setPlatformConfigured(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canManage]);

  const loadSession = React.useCallback(async (accountId: string, options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoadingSession(true);
    }
    setError(null);
    try {
      const next = await getWhatsAppAppSession(accountId);
      setSession(previous => {
        const status = next.connectionStatus?.toLowerCase();
        if (status === 'open') {
          return next;
        }

        return {
          ...next,
          qrCodeBase64: next.qrCodeBase64 || previous?.qrCodeBase64 || null,
        };
      });
      return next;
    } catch (loadError) {
      if (!options?.silent) {
        setSession(null);
        setError(loadError instanceof Error ? loadError.message : t('failedToLoadWhatsAppAppSession', { ns: 'companies' }));
      }
      return null;
    } finally {
      if (!options?.silent) {
        setLoadingSession(false);
      }
    }
  }, [t]);

  React.useEffect(() => {
    const accountId = accounts.selectedAccountId;
    if (!accountId) {
      setSession(null);
      return;
    }

    void loadSession(accountId);
  }, [accounts.selectedAccountId, loadSession]);

  React.useEffect(() => {
    const accountId = accounts.selectedAccountId;
    const status = session?.connectionStatus?.toLowerCase();
    const waitingForQr = Boolean(accountId && status && status !== 'open' && status !== 'close');
    const watchLinkedLine = status === 'open';
    if ((!waitingForQr && !watchLinkedLine) || !accountId) {
      return;
    }

    const timer = window.setInterval(() => {
      void loadSession(accountId, { silent: true });
    }, watchLinkedLine ? CONNECTED_POLL_MS : POLL_MS);

    return () => window.clearInterval(timer);
  }, [accounts.selectedAccountId, loadSession, session?.connectionStatus]);

  const createAccount = async () => {
    const name = displayName.trim();
    if (!name) {
      const message = t('whatsappAppDisplayNameRequired', { ns: 'companies' });
      setError(message);
      showToast(message, 'error');
      return;
    }

    setCreating(true);
    setError(null);
    setNotice(null);

    try {
      const created = await createWhatsAppAppChannelAccount({ displayName: name, isDefault: accounts.accounts.length === 0 });
      setSession(created);
      setDisplayName('');
      setShowAddForm(false);
      await accounts.reload();
      accounts.setSelectedAccountId(created.account.id);
      const message = t('whatsappAppAccountCreated', { ns: 'companies' });
      setNotice(message);
      showToast(message, 'success');
    } catch (createError) {
      const message = createError instanceof Error ? createError.message : t('failedToCreateWhatsAppAppAccount', { ns: 'companies' });
      setError(message);
      showToast(message, 'error');
    } finally {
      setCreating(false);
    }
  };

  const reconnect = React.useCallback(async () => {
    const accountId = accounts.selectedAccountId;
    if (!accountId) {
      return;
    }

    setRefreshingQr(true);
    setError(null);
    setNotice(null);
    try {
      const next = await refreshWhatsAppAppQr(accountId);
      setSession(next);
    } catch (refreshError) {
      const message = refreshError instanceof Error ? refreshError.message : t('failedToRefreshWhatsAppAppQr', { ns: 'companies' });
      setError(message);
      showToast(message, 'error');
    } finally {
      setRefreshingQr(false);
    }
  }, [accounts.selectedAccountId, showToast, t]);

  React.useEffect(() => {
    if (!welcome) {
      return;
    }

    const accountId = accounts.selectedAccountId;
    const status = session?.connectionStatus?.toLowerCase();
    if (!accountId || loadingSession || !session || session.qrCodeBase64 || status === 'open' || status === 'close') {
      return;
    }

    if (autoQrAccountId.current === accountId) {
      return;
    }

    autoQrAccountId.current = accountId;
    void reconnect();
  }, [accounts.selectedAccountId, loadingSession, reconnect, session, welcome]);

  const disconnect = async () => {
    const accountId = accounts.selectedAccountId;
    if (!accountId) {
      return;
    }

    setDisconnecting(true);
    setError(null);
    setNotice(null);
    try {
      const next = await logoutWhatsAppApp(accountId);
      setSession(next);
      await accounts.reload();
      const message = t('whatsappAppDisconnected', { ns: 'companies' });
      setNotice(message);
      showToast(message, 'success');
    } catch (logoutError) {
      const message = logoutError instanceof Error ? logoutError.message : t('failedToDisconnectWhatsAppApp', { ns: 'companies' });
      setError(message);
      showToast(message, 'error');
    } finally {
      setDisconnecting(false);
    }
  };

  if (!canManage) {
    return null;
  }

  const connected = session?.connectionStatus?.toLowerCase() === 'open';
  const qrSrc = qrImageSrc(session?.qrCodeBase64);
  const statusLabel = t(`whatsappAppStatus.${session?.connectionStatus ?? 'connecting'}`, {
    ns: 'companies',
    defaultValue: session?.connectionStatus ?? t('whatsappAppStatus.connecting', { ns: 'companies' }),
  });
  const showStage = welcome ? !accounts.loading : Boolean(accounts.selectedAccountId);
  const showAccountBar = !welcome || accounts.accounts.length > 1;

  const nameForm = (
    <div className={styles.nameForm}>
      <label>
        {t('whatsappAppDisplayName', { ns: 'companies' })}
        <input
          className="text-input"
          value={displayName}
          onChange={event => setDisplayName(event.target.value)}
          disabled={creating}
          placeholder={t('whatsappAppDisplayNamePlaceholder', { ns: 'companies' })}
        />
      </label>
      <button type="button" className="btn btn-primary" onClick={() => { void createAccount(); }} disabled={creating}>
        {creating ? t('savingEllipsis', { ns: 'admin' }) : t('whatsappAppCreateAccount', { ns: 'companies' })}
      </button>
    </div>
  );

  return (
    <section className={`company-section ${styles.panel}`}>
      {showAccountBar ? (
        <ChannelAccountBar
          channel="WhatsApp"
          connectionKind="app"
          accounts={accounts.accounts}
          limits={accounts.limits}
          loading={accounts.loading}
          error={accounts.error}
          selectedAccountId={accounts.selectedAccountId}
          onSelectAccount={accounts.setSelectedAccountId}
          onRefresh={() => { void accounts.reload(); }}
          canManage={canManage}
          showAddForm={showAddForm}
          onToggleAddForm={() => setShowAddForm(current => !current)}
          onAccountUpdated={() => { void accounts.reload(); }}
        />
      ) : null}

      {!welcome && showAddForm ? (
        <div className="company-card" style={{ marginTop: 16 }}>
          <h4>{t('whatsappAppAddAccountHeading', { ns: 'companies' })}</h4>
          {nameForm}
        </div>
      ) : null}

      {platformConfigured === false ? (
        <div className="error" style={{ marginTop: 16 }}>{t('whatsappAppNotConfigured', { ns: 'companies' })}</div>
      ) : null}

      {error ? <div className="error" style={{ marginTop: 16 }}>{error}</div> : null}
      {notice ? <div className="success" style={{ marginTop: 16 }}>{notice}</div> : null}
      {welcome && accounts.loading ? <Spinner text={t('loadingWhatsAppAppSession', { ns: 'companies' })} /> : null}

      {showStage ? (
        <div
          className={`${styles.stage} ${welcome ? styles.stageWelcome : ''}`}
          role="region"
          aria-label={t('whatsappAppScanTitle', { ns: 'companies' })}
        >
          <p className={styles.kicker}>{t('whatsappAppScanKicker', { ns: 'companies' })}</p>
          <h3 className={styles.title}>
            {connected ? t('whatsappAppConnected', { ns: 'companies' }) : t('whatsappAppScanTitle', { ns: 'companies' })}
          </h3>
          {connected ? null : (
            <p className={styles.lead}>{t('whatsappAppScanLead', { ns: 'companies' })}</p>
          )}
          {connected ? null : (
            <ol className={styles.steps}>
              {[1, 2, 3].map(step => (
                <li key={step}>
                  <span className={styles.stepNumber}>{step}</span>
                  {t(`whatsappAppScanStep${step}`, { ns: 'companies' })}
                </li>
              ))}
            </ol>
          )}

          {connected ? (
            <div className={styles.success}>
              {session?.linkedPhoneNumber
                ? t('whatsappAppConnectedPhone', { ns: 'companies', phone: session.linkedPhoneNumber })
                : t('whatsappAppConnected', { ns: 'companies' })}
            </div>
          ) : qrSrc ? (
            <>
              <div className={styles.frame}>
                <img src={qrSrc} alt={t('whatsappAppQrAlt', { ns: 'companies' })} />
              </div>
              <div className="company-actions-inline" style={{ justifyContent: 'center', marginTop: 14 }}>
                <button type="button" className="btn btn-secondary" onClick={() => { void reconnect(); }} disabled={refreshingQr}>
                  {refreshingQr ? t('whatsappAppGettingQr', { ns: 'companies' }) : t('whatsappAppReconnect', { ns: 'companies' })}
                </button>
              </div>
            </>
          ) : (
            <div className={styles.placeholder}>
              {loadingSession || refreshingQr || creating ? (
                <Spinner text={t('whatsappAppGettingQr', { ns: 'companies' })} />
              ) : accounts.selectedAccountId ? (
                <>
                  <p className={styles.placeholderTitle}>{t('whatsappAppShowQrTitle', { ns: 'companies' })}</p>
                  <p className={styles.placeholderText}>{t('whatsappAppShowQrHelp', { ns: 'companies' })}</p>
                  <button type="button" className="btn btn-primary" onClick={() => { void reconnect(); }} disabled={refreshingQr}>
                    {t('whatsappAppShowQr', { ns: 'companies' })}
                  </button>
                </>
              ) : (
                <>
                  <p className={styles.placeholderTitle}>{t('whatsappAppNameFirstTitle', { ns: 'companies' })}</p>
                  <p className={styles.placeholderText}>{t('whatsappAppNameFirst', { ns: 'companies' })}</p>
                  {nameForm}
                </>
              )}
            </div>
          )}
        </div>
      ) : null}

      {accounts.selectedAccountId && !loadingSession ? (
        <div className={styles.details}>
          <p><strong>{t('whatsappAppConnectionStatus', { ns: 'companies' })}:</strong> {statusLabel}</p>
          {session?.linkedPhoneNumber ? (
            <p><strong>{t('whatsappAppLinkedPhone', { ns: 'companies' })}:</strong> {session.linkedPhoneNumber}</p>
          ) : null}
          {connected ? (
            <button type="button" className="btn btn-secondary" onClick={() => { void disconnect(); }} disabled={disconnecting}>
              {disconnecting ? t('savingEllipsis', { ns: 'admin' }) : t('whatsappAppDisconnect', { ns: 'companies' })}
            </button>
          ) : null}
          <p className={styles.warning}>{t('whatsappAppWarning', { ns: 'companies' })}</p>
        </div>
      ) : null}
    </section>
  );
};
